<script setup>
// The documents' search and filters (phase 4.3): the kit's ListToolbar — the search, "Source is …" and "Status is …"
// as pills, and the sort as an OptionSelect beside them. Same three choices and same rules as before (a status
// only exists for web pages, so choosing one picks "Web pages"; choosing PDFs clears the status and hides it), same
// request params and the same exposed API for the page.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';

defineProps({
  count: { type: Number, default: null },
});
const emit = defineEmits(['change']);
const search = defineModel('search', { type: String, default: '' });

const { t } = useI18n();

const source = ref('');
const status = ref('');
const sort = ref('recently_updated');

const hasActiveFilters = computed(() => !!source.value || !!status.value);

const filters = computed(() => [
  {
    id: 'source',
    label: t('POLARIS.PAGES.DOCUMENT_FILTERS.SOURCE'),
    options: [
      { value: 'web', label: t('POLARIS.DOCUMENTS.FILTERS.SOURCE.WEB') },
      { value: 'pdf', label: t('POLARIS.DOCUMENTS.FILTERS.SOURCE.PDF') },
    ],
  },
  ...(source.value === 'pdf'
    ? []
    : [
        {
          id: 'status',
          label: t('POLARIS.PAGES.DOCUMENT_FILTERS.STATUS'),
          options: [
            {
              value: 'synced',
              label: t('POLARIS.DOCUMENTS.FILTERS.STATUS.UPDATED'),
            },
            {
              value: 'stale',
              label: t('POLARIS.DOCUMENTS.FILTERS.STATUS.NEEDS_UPDATE'),
            },
            {
              value: 'syncing',
              label: t('POLARIS.DOCUMENTS.FILTERS.STATUS.UPDATING'),
            },
            {
              value: 'failed',
              label: t('POLARIS.DOCUMENTS.FILTERS.STATUS.FAILED'),
            },
          ],
        },
      ]),
]);

const sortOptions = computed(() => [
  {
    value: 'recently_updated',
    label: t('POLARIS.DOCUMENTS.FILTERS.SORT.RECENTLY_UPDATED'),
  },
  {
    value: 'recently_created',
    label: t('POLARIS.DOCUMENTS.FILTERS.SORT.RECENTLY_CREATED'),
  },
]);

const chosen = computed({
  get: () => ({ source: source.value, status: status.value }),
  set: next => {
    if (next.source !== source.value) {
      source.value = next.source || '';
      if (source.value !== 'web') status.value = '';
    } else if (next.status !== status.value) {
      status.value = next.status || '';
      if (status.value) source.value = 'web';
    }
    emit('change');
  },
});

const onSort = value => {
  sort.value = value;
  emit('change');
};

const buildParams = (page = 1) => {
  const params = { page };
  if (source.value) params.source = source.value;
  if (status.value) params.filter = status.value;
  if (sort.value) params.sort = sort.value;
  return params;
};

const reset = () => {
  source.value = '';
  status.value = '';
  sort.value = 'recently_updated';
};

defineExpose({ buildParams, reset, hasActiveFilters });
</script>

<template>
  <ListToolbar
    v-model:search="search"
    v-model:chosen="chosen"
    :placeholder="t('POLARIS.DOCUMENTS.FILTERS.SEARCH_PLACEHOLDER')"
    :filters="filters"
    :count="count"
    count-key="POLARIS.PAGES.DOCUMENT_FILTERS.COUNT"
  >
    <template #extra>
      <!-- OptionSelect's trigger is full width; the wrapper gives it the width of a toolbar control. -->
      <div class="w-48">
        <OptionSelect
          :model-value="sort"
          :options="sortOptions"
          size="sm"
          :aria-label="t('POLARIS.PAGES.DOCUMENT_FILTERS.SORT')"
          @update:model-value="onSort"
        />
      </div>
    </template>
  </ListToolbar>
</template>
